import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import { Save, MessageCircle, Code2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  {
    key: "tawk",
    label: "Tawk.to",
    hint: "Dashboard → Administration → Chat Widget → copy widget code.",
    template: `<!--Start of Tawk.to Script-->
<script type="text/javascript">
var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date();
(function(){
var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
s1.async=true;
s1.src='https://embed.tawk.to/REPLACE_WITH_YOUR_PROPERTY_ID/REPLACE_WITH_YOUR_WIDGET_ID';
s1.charset='UTF-8';
s1.setAttribute('crossorigin','*');
s0.parentNode.insertBefore(s1,s0);
})();
</script>
<!--End of Tawk.to Script-->`,
  },
  {
    key: "crisp",
    label: "Crisp",
    hint: "Settings → Website Settings → Setup Instructions → copy installation code.",
    template: `<script type="text/javascript">
window.$crisp=[];window.CRISP_WEBSITE_ID="REPLACE_WITH_YOUR_WEBSITE_ID";
(function(){
var d=document;var s=d.createElement("script");
s.src="https://client.crisp.chat/l.js";s.async=1;
d.getElementsByTagName("head")[0].appendChild(s);
})();
</script>`,
  },
  {
    key: "tidio",
    label: "Tidio",
    hint: "Settings → Channels → Live Chat → Installation → copy script.",
    template: `<script src="//code.tidio.co/REPLACE_WITH_YOUR_PUBLIC_KEY.js" async></script>`,
  },
  {
    key: "jivochat",
    label: "JivoChat",
    hint: "Manage → Channels → Installation Code.",
    template: `<script src="//code.jivosite.com/widget/REPLACE_WITH_YOUR_WIDGET_ID" async></script>`,
  },
  {
    key: "intercom",
    label: "Intercom",
    hint: "Settings → Installation → Web → copy snippet (replace APP_ID).",
    template: `<script>
window.intercomSettings={app_id:"REPLACE_WITH_YOUR_APP_ID"};
(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/'+w.intercomSettings.app_id;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
</script>`,
  },
  {
    key: "zendesk",
    label: "Zendesk Web Widget",
    hint: "Admin Center → Channels → Web Widget → install code.",
    template: `<script id="ze-snippet" src="https://static.zdassets.com/ekr/snippet.js?key=REPLACE_WITH_YOUR_WIDGET_KEY"></script>`,
  },
  {
    key: "custom",
    label: "Custom / Other",
    hint: "Paste any HTML / <script> block from your provider below.",
    template: "",
  },
];

export default function ConfigLiveChat() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings/live-chat");
      setForm(data);
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPreset = (preset) => {
    if (!preset.template) return;
    if (form?.embed_code && !window.confirm("Replace current embed code with this template?")) return;
    setForm((f) => ({ ...f, provider: preset.label, embed_code: preset.template }));
    toast.message(`Loaded ${preset.label} template — replace placeholders with your IDs.`);
  };

  const save = async () => {
    if (form.enabled && !form.embed_code?.trim()) {
      toast.error("Embed code is required when live chat is enabled.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/live-chat", {
        enabled: !!form.enabled,
        provider: form.provider || "",
        embed_code: form.embed_code || "",
        position: form.position || "bottom-right",
        show_header_button: form.show_header_button !== false,
        show_floating_button: form.show_floating_button !== false,
      });
      setForm(data);
      toast.success("Live chat settings saved. Visitors will see the widget refresh shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;
  }

  return (
    <div data-testid="config-live-chat-page">
      <PageHeader
        title="Configuration"
        subtitle="Site-wide configuration · Live Chat · paste any 3rd-party widget script"
      />
      <ConfigTabs />

      <form
        onSubmit={(e) => { e.preventDefault(); save(); }}
        className="bg-white border border-gray-200 rounded-sm p-6 max-w-5xl space-y-6"
        data-testid="config-live-chat-form"
      >
        {/* Top toggle */}
        <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-sm bg-gray-50">
          <div className="h-10 w-10 rounded-sm bg-emerald-500/10 text-emerald-600 inline-flex items-center justify-center shrink-0">
            <MessageCircle size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-gray-900">Enable Live Chat on Website</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  When enabled, the embed code below is injected into every public page.
                </div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={(e) => set("enabled", e.target.checked)}
                  className="sr-only peer"
                  data-testid="lc-enabled"
                />
                <span className="relative w-11 h-6 bg-gray-300 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Quick presets */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Quick-start templates
          </label>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p)}
                title={p.hint}
                data-testid={`lc-preset-${p.key}`}
                className="h-9 px-3 border border-gray-200 rounded-sm text-sm font-medium text-gray-700 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-gray-500 mt-2">
            Tip: Pick a template to pre-fill the embed code, then replace the placeholder ID(s) with your account values. For any provider not listed, choose <b>Custom / Other</b> and paste the snippet below.
          </div>
        </div>

        {/* Provider + position */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Provider name (label)</label>
            <input
              value={form.provider || ""}
              onChange={(e) => set("provider", e.target.value)}
              placeholder="e.g. Tawk.to, Crisp, Custom"
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
              data-testid="lc-provider"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Widget position (informational)</label>
            <select
              value={form.position || "bottom-right"}
              onChange={(e) => set("position", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
              data-testid="lc-position"
            >
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
            </select>
          </div>
        </div>

        {/* Embed code */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
              <Code2 size={13} /> Embed code (HTML / &lt;script&gt;)
            </label>
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1"
              data-testid="lc-toggle-preview"
            >
              {showPreview ? <><EyeOff size={12} /> Hide preview</> : <><Eye size={12} /> Show preview</>}
            </button>
          </div>
          <textarea
            value={form.embed_code || ""}
            onChange={(e) => set("embed_code", e.target.value)}
            rows={14}
            spellCheck={false}
            placeholder="<!-- Paste your provider's full installation snippet here -->"
            className="w-full px-3 py-2 border border-gray-200 rounded-sm text-[12px] leading-5 font-mono bg-gray-50 focus:outline-none focus:border-emerald-500"
            data-testid="lc-embed-code"
          />
          <div className="text-[11px] text-gray-500 mt-1.5">
            Anything you paste here will be injected verbatim into the public site (scripts execute, HTML elements render). Only paste code from sources you trust.
          </div>
          {showPreview && form.embed_code && (
            <pre className="mt-3 p-3 bg-[#0b1020] text-[11px] text-emerald-200 rounded-sm overflow-auto max-h-72 whitespace-pre-wrap break-all">
{form.embed_code}
            </pre>
          )}
        </div>

        {/* Header button toggle */}
        <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm">
          <input
            id="lc-show-header"
            type="checkbox"
            checked={form.show_header_button !== false}
            onChange={(e) => set("show_header_button", e.target.checked)}
            className="mt-1"
            data-testid="lc-show-header"
          />
          <label htmlFor="lc-show-header" className="cursor-pointer">
            <div className="text-sm font-semibold text-gray-900">Keep "24/7 Live Chat" button in site header</div>
            <div className="text-xs text-gray-500 mt-0.5">
              When checked, the existing header button stays visible. Most 3rd-party widgets show their own floating bubble — disable this if you want only the floating bubble.
            </div>
          </label>
        </div>

        {/* Floating Max_Ai chat bubble toggle */}
        <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm">
          <input
            id="lc-show-floating"
            type="checkbox"
            checked={form.show_floating_button !== false}
            onChange={(e) => set("show_floating_button", e.target.checked)}
            className="mt-1"
            data-testid="lc-show-floating"
          />
          <label htmlFor="lc-show-floating" className="cursor-pointer">
            <div className="text-sm font-semibold text-gray-900">Show floating Max_Ai live-chat bubble on website</div>
            <div className="text-xs text-gray-500 mt-0.5">
              When checked, the green headset bubble (in-app Max_Ai assistant + human handoff) is rendered bottom-right on every public page. Uncheck to hide it completely — useful when you only want a 3rd-party widget.
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Last updated: {form.updated_at ? new Date(form.updated_at).toLocaleString() : "—"}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="lc-save"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Live Chat Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
